import { HorizontalContainer, VerticalContainer } from '@/components';
import { NewbornProgram } from '@/features/programs/model/types';
import {
    R,
    format,
} from '@/features/programs/widgets/newborn/resources/NewbornResources.resx';
import { Utils } from '@/shared/Utils';
import {
    AubergineIcon,
    BlueBoxTickIcon,
    LemonIcon,
    Newborn0to3Icon,
    Newborn3to6Icon,
    Newborn6to9Icon,
    Newborn9to12Icon,
    SesameSeedIcon,
    WatermelonIcon,
} from '@/components/Icons';
import { differenceInMonths } from 'date-fns';

type InfoSection = {
    header: string;
    description: string;
    icon1: React.JSX.Element;
    icon2: React.JSX.Element;
    icon3: React.JSX.Element | null;
    infoBox1Header: string;
    infoBox1BulletPoints: (string | React.JSX.Element)[];
    infoBox2Header: string;
    infoBox2BulletPoints: (string | React.JSX.Element)[];
};

type InfoSections = {
    [key in Stages]: InfoSection;
};

enum Stages {
    FirstTrimester = 0,
    SecondTrimester = 1,
    ThirdTrimester = 2,
    ZeroToThreeMonths = 3,
    ThreeToSixMonths = 4,
    SixToNineMonths = 5,
    NineToTwelveMonths = 6,
}

const firstTrimesterPregnantLink = 'www.placeholder.dk';
const firstTrimesterPartnerLink = 'www.placeholder.dk';

const infoSections: InfoSections = {
    [Stages.FirstTrimester]: {
        header: format(R.InfoSectionHeader, Utils.ordinalNumber(1), '4', '12'),
        description: R.FirstTrimesterDescription,
        icon1: <SesameSeedIcon className="size-17 p-4" />,
        icon2: <LemonIcon className="size-25" />,
        icon3: <AubergineIcon className="size-17" />,
        infoBox1Header: R.PregnantInfoHeader,
        infoBox1BulletPoints: [
            <div>
                <a className="underline" href={firstTrimesterPregnantLink}>
                    {R.ReadAboutLink}
                </a>{' '}
                {R.PregnantInfoBullet1}
            </div>,
            format(R.PregnantInfoBullet2, Utils.ordinalNumber(1)),
            R.PregnantInfoBullet3,
        ],
        infoBox2Header: R.PartnerInfoHeader,
        infoBox2BulletPoints: [
            <div>
                <a className="underline" href={firstTrimesterPartnerLink}>
                    {R.ReadAboutLink}
                </a>{' '}
                {format(R.PregnancyPartnerInfoBullet1, Utils.ordinalNumber(1))}
            </div>,
            R.PregnancyPartnerInfoBullet2,
            R.PregnancyPartnerInfoBullet3,
        ],
    },
    [Stages.SecondTrimester]: {
        header: format(R.InfoSectionHeader, Utils.ordinalNumber(2), '13', '28'),
        description: R.SecondTrimesterDescription,
        icon1: <LemonIcon className="size-17" />,
        icon2: <AubergineIcon className="size-25" />,
        icon3: <WatermelonIcon className="size-17" />,
        infoBox1Header: R.PregnantInfoHeader,
        infoBox1BulletPoints: [
            <div>
                <a className="underline" href={firstTrimesterPregnantLink}>
                    {R.ReadAboutLink}
                </a>{' '}
                {R.PregnantInfoBullet1}
            </div>,
            format(R.PregnantInfoBullet2, Utils.ordinalNumber(2)),
            R.PregnantInfoBullet3,
        ],
        infoBox2Header: R.PartnerInfoHeader,
        infoBox2BulletPoints: [
            <div>
                <a className="underline" href={firstTrimesterPartnerLink}>
                    {R.ReadAboutLink}
                </a>{' '}
                {format(R.PregnancyPartnerInfoBullet1, Utils.ordinalNumber(2))}
            </div>,
            R.PregnancyPartnerInfoBullet2,
            R.PregnancyPartnerInfoBullet3,
        ],
    },
    [Stages.ThirdTrimester]: {
        header: format(R.InfoSectionHeader, Utils.ordinalNumber(3), '29', '40'),
        description: R.ThirdTrimesterDescription,
        icon1: <AubergineIcon className="size-17" />,
        icon2: <WatermelonIcon className="size-25" />,
        icon3: <Newborn0to3Icon className="size-17" />,
        infoBox1Header: R.PregnantInfoHeader,
        infoBox1BulletPoints: [
            <div>
                <a className="underline" href={firstTrimesterPregnantLink}>
                    {R.ReadAboutLink}
                </a>{' '}
                {R.PregnantInfoBullet1}
            </div>,
            format(R.PregnantInfoBullet2, Utils.ordinalNumber(3)),
            R.PregnantInfoBullet3,
        ],
        infoBox2Header: R.PartnerInfoHeader,
        infoBox2BulletPoints: [
            <div>
                <a className="underline" href={firstTrimesterPartnerLink}>
                    {R.ReadAboutLink}
                </a>{' '}
                {format(R.PregnancyPartnerInfoBullet1, Utils.ordinalNumber(3))}
            </div>,
            R.PregnancyPartnerInfoBullet2,
            R.PregnancyPartnerInfoBullet3,
        ],
    },
    [Stages.ZeroToThreeMonths]: {
        header: R.ZeroToThreeMonthsHeader,
        description: R.ZeroToThreeMonthsDescription,
        icon1: <WatermelonIcon className="size-17" />,
        icon2: <Newborn0to3Icon className="size-25" />,
        icon3: <Newborn3to6Icon className="size-17" />,
        infoBox1Header: R.GivenBirthInfoHeader,
        infoBox1BulletPoints: [],
        infoBox2Header: R.PartnerInfoHeader,
        infoBox2BulletPoints: [],
    },
    [Stages.ThreeToSixMonths]: {
        header: format(R.ThreeToTwelveMonthsHeader, '3', '6'),
        description: R.ThreeToSixMonthsDescription,
        icon1: <Newborn0to3Icon className="size-17" />,
        icon2: <Newborn3to6Icon className="size-25" />,
        icon3: <Newborn6to9Icon className="size-17" />,
        infoBox1Header: R.GivenBirthInfoHeader,
        infoBox1BulletPoints: [],
        infoBox2Header: R.PartnerInfoHeader,
        infoBox2BulletPoints: [],
    },
    [Stages.SixToNineMonths]: {
        header: format(R.ThreeToTwelveMonthsHeader, '6', '9'),
        description: R.SixToNineMonthsDescription,
        icon1: <Newborn3to6Icon className="size-17" />,
        icon2: <Newborn6to9Icon className="size-25" />,
        icon3: <Newborn9to12Icon className="size-17" />,
        infoBox1Header: R.GivenBirthInfoHeader,
        infoBox1BulletPoints: [],
        infoBox2Header: R.PartnerInfoHeader,
        infoBox2BulletPoints: [],
    },
    [Stages.NineToTwelveMonths]: {
        header: format(R.ThreeToTwelveMonthsHeader, '9', '12'),
        description: R.NineToTwelveMonthsDescription,
        icon1: <Newborn6to9Icon className="size-17" />,
        icon2: <Newborn9to12Icon className="size-25" />,
        icon3: null,
        infoBox1Header: R.GivenBirthInfoHeader,
        infoBox1BulletPoints: [],
        infoBox2Header: R.PartnerInfoHeader,
        infoBox2BulletPoints: [],
    },
};

export const NewbornInfoSection = ({
    newbornProgram,
}: {
    newbornProgram: NewbornProgram;
}) => {
    const getInfoSection = (): InfoSection => {
        if (newbornProgram.IsBabyBorn) {
            const monthsSinceBirth = differenceInMonths(
                new Date(),
                new Date(newbornProgram.DateOfBirth),
            );

            return monthsSinceBirth < 3
                ? infoSections[Stages.ZeroToThreeMonths]
                : monthsSinceBirth < 6
                  ? infoSections[Stages.ThreeToSixMonths]
                  : monthsSinceBirth < 9
                    ? infoSections[Stages.SixToNineMonths]
                    : infoSections[Stages.NineToTwelveMonths];
        }

        return newbornProgram.PregnancyWeek < 13
            ? infoSections[Stages.FirstTrimester]
            : newbornProgram.PregnancyWeek < 29
              ? infoSections[Stages.SecondTrimester]
              : infoSections[Stages.ThirdTrimester];
    };

    const data = getInfoSection();

    return (
        <VerticalContainer spacing="lg" className="items-center p-4 py-10">
            <VerticalContainer
                spacing="md"
                className="max-w-lg items-center text-center"
            >
                <h1>{data.header}</h1>
                <div>{data.description}</div>

                <HorizontalContainer
                    spacing="none"
                    className="my-4 w-full items-center justify-center"
                >
                    <div className="border-secondary-blue-dark shrink-0 overflow-hidden rounded-full border-2 opacity-50">
                        {data.icon1}
                    </div>
                    <div className="bg-secondary-blue-dark h-0.5 w-full max-w-2/8"></div>
                    <div className="border-secondary-blue-dark shrink-0 overflow-hidden rounded-full border-2">
                        {data.icon2}
                    </div>
                    {data.icon3 && (
                        <>
                            <div className="bg-secondary-blue-dark h-0.5 w-full"></div>
                            <div className="border-secondary-blue-dark shrink-0 overflow-hidden rounded-full border-2 opacity-50">
                                {data.icon3}
                            </div>
                        </>
                    )}
                </HorizontalContainer>
            </VerticalContainer>

            {/* Hidden for now, will be implemented once the texts are ready (9/10/2026) */}
            <HorizontalContainer
                spacing="sm"
                className="hidden max-w-[820px] flex-wrap items-center justify-center"
            >
                <VerticalContainer
                    spacing="md"
                    className="bg-bg-surface max-w-[400px] rounded-2xl p-6"
                >
                    <h2>{data.infoBox1Header}</h2>
                    <ul className="flex flex-col gap-3 text-sm">
                        {data.infoBox1BulletPoints.map((bulletPoint, index) => (
                            <HorizontalContainer
                                key={index}
                                spacing="sm"
                                className="w-full items-center"
                            >
                                <BlueBoxTickIcon className="size-8" />
                                <li key={index}>{bulletPoint}</li>
                            </HorizontalContainer>
                        ))}
                    </ul>
                </VerticalContainer>

                <VerticalContainer
                    spacing="md"
                    className="bg-bg-surface max-w-[400px] rounded-2xl p-6"
                >
                    <h2>{data.infoBox1Header}</h2>
                    <ul className="flex flex-col gap-3 text-sm">
                        {data.infoBox1BulletPoints.map((bulletPoint, index) => (
                            <HorizontalContainer
                                key={index}
                                spacing="md"
                                className="w-full items-center"
                            >
                                <BlueBoxTickIcon className="size-8" />
                                <li key={index}>{bulletPoint}</li>
                            </HorizontalContainer>
                        ))}
                    </ul>
                </VerticalContainer>
            </HorizontalContainer>
        </VerticalContainer>
    );
};
